import { getFieldMapping } from '../utils/util';

/**
 * Stock status enumeration
 */
export const StockStatus = Object.freeze({
  OUT_OF_STOCK: 0,
  IN_STOCK: 1,
  BACKORDER: 2,
  NOT_AVAILABLE: 3
});

/**
 * Stock availability service
 * Centralized logic for determining product availability
 */
export class AvailabilityService {
  /**
   * Get stock status for an item
   * @param {Object} item - Product item object
   * @param {string} userType - User type ('B2B' or 'B2C')
   * @param {string} culture - Culture code
   * @returns {number} Stock status enum value
   */
  static getStockStatus(item, userType, culture) {
    if (!item) {
      console.log("AvailabilityService: no item");
      return StockStatus.OUT_OF_STOCK;
    }

    const itemSellableField = getFieldMapping(culture);
    if (!itemSellableField) {
      console.log("AvailabilityService: no item sellable field");
      return StockStatus.NOT_AVAILABLE;
    }

    const isBTCSellable = item[itemSellableField + 'dtc'] === 'Sellable' || item[itemSellableField + 'b2c'] === 'Sellable';
    const isB2bSellable = item[itemSellableField + 'b2b'] === 'Sellable';

    console.log("AvailabilityService:", item, itemSellableField, item[itemSellableField + 'dtc'], item[itemSellableField + 'b2c'], item[itemSellableField + 'b2b']);

    // Check if item is sellable for user type
    if (!isBTCSellable && userType !== 'B2B') {
      console.log("AvailabilityService: not btc sellable");
      return StockStatus.NOT_AVAILABLE;
    }

    if (!isB2bSellable && userType === 'B2B') {
      console.log("AvailabilityService: not b2b sellable");
      return StockStatus.NOT_AVAILABLE;
    }

    // B2B specific logic
    if (userType === 'B2B') {
      if (!item.isinstock && !isBTCSellable && !item.isbackorderable) {
        console.log("AvailabilityService: out of stock");
        return StockStatus.OUT_OF_STOCK;
      }
      // Backorder: not in stock but sellable and backorderable
      if (!item.isinstock && isBTCSellable && item.isbackorderable) {
        console.log("AvailabilityService: backorder");
        return StockStatus.BACKORDER;
      }
    }

    // In stock
    if (item.isinstock) {
      console.log("AvailabilityService: in stock");
      return StockStatus.IN_STOCK;
    }

    console.log("AvailabilityService: default out of stock");
    return StockStatus.OUT_OF_STOCK;
  }

  /**
   * Get stock status for AU/NZ region
   * @param {string} availabilityAU - Availability status from AU API
   * @param {string} userType - User type ('B2B' or 'B2C')
   * @returns {number} Stock status enum value
   */
  static getStockStatusAU(availabilityAU, userType) {
    if (availabilityAU === 'IN_STOCK') {
      return StockStatus.IN_STOCK;
    }
    if (availabilityAU === 'BACKORDER' && userType === 'B2B') {
      return StockStatus.BACKORDER;
    }
    if (availabilityAU === 'CUSTOM_BUILD' || availabilityAU === 'OUT_OF_STOCK') {
      return StockStatus.OUT_OF_STOCK;
    }
    return StockStatus.OUT_OF_STOCK;
  }

  /**
   * Get CSS class for stock status
   * @param {number} status - Stock status enum value
   * @returns {string} CSS class name
   */
  static getStatusClass(status) {
    switch (status) {
      case StockStatus.OUT_OF_STOCK:
        return 'out_of_stock';
      case StockStatus.BACKORDER:
        return 'available_for_backorder';
      case StockStatus.IN_STOCK:
        return 'in_store';
      case StockStatus.NOT_AVAILABLE:
        return false;
      default:
        return 'out_of_stock';
    }
  }

  /**
   * Get display text for stock status
   * @param {number} status - Stock status enum value
   * @param {Object} labels - Label object with status text
   * @returns {string|boolean} Display text or false if not available
   */
  static getStatusText(status, labels) {
    switch (status) {
      case StockStatus.OUT_OF_STOCK:
        return labels.out_of_stock || 'Out of Stock';
      case StockStatus.BACKORDER:
        return labels.available_for_backorder || 'Available for Backorder';
      case StockStatus.IN_STOCK:
        return labels.in_stock || 'In Stock';
      case StockStatus.NOT_AVAILABLE:
        return false;
      default:
        return labels.out_of_stock || 'Out of Stock';
    }
  }

  /**
   * Check if package is in stock
   * @param {Array} components - Array of component items
   * @returns {boolean} True if all components are in stock
   */
  static isPackageInStock(components) {
    if (!components || components.length === 0) {
      return false;
    }
    return components.every(item => item.isinstock === true);
  }

  /**
   * Check if package is backorderable
   * @param {Array} components - Array of component items
   * @returns {boolean} True if all components are backorderable
   */
  static isPackageBackOrderable(components) {
    if (!components || components.length === 0) {
      return false;
    }
    return components.every(item => item.isbackorderable === true);
  }
}

